<template>
    <div>
        <breadcrumb
            class="crumbs col-sm-10"
            :steps="steps"
            :active="currentStep"
            @onNavigate="step => (currentStep = step)"
        ></breadcrumb>
        <div class="container col-sm-10">
            <form v-if="currentStep === 0" @submit.prevent="saveStep" name="step0">
                <h4 class="section-header">Set up your account settings</h4>
                <div class="row">
                    <div class="form-group col-sm-6">
                        <photo-upload-widget
                            :user="user"
                            :profile-status="{ has_photo: profile_photo_path }"
                        ></photo-upload-widget>
                    </div>
                </div>

                <div class="row">
                    <div class="form-group col-sm-5">
                        <label for="title">Title</label>
                        <select v-model="title" id="title" class="form-control" required>
                            <option selected disabled>Please select a type</option>
                            <option v-for="title in titles" :value="title">{{ title }}</option>
                            <option value="other">Other (specify)</option>
                        </select>
                        <input
                            v-if="title && !titles.includes(title)"
                            v-model="title_other"
                            placeholder="Specify title"
                            name="title_other"
                            type="text"
                            class="form-control mt-2"
                            required
                        />
                    </div>
                </div>

                <div class="row">
                    <div class="form-group col-sm-6">
                        <label>Phone number</label>
                        <input
                            v-mask="'(###) ###-####'"
                            v-model="primary_phone"
                            name="phone"
                            type="text"
                            class="form-control"
                            required
                        />
                    </div>
                </div>

                <div class="form-group">
                    <label for="biography">Biography</label>
                    <textarea
                        v-model="biography"
                        id="biography"
                        class="form-control"
                        rows="5"
                        placeholder="The academic and professional background that lead you to become who you are today."
                    ></textarea>
                </div>

                <div class="form-group">
                    <label>Address</label>
                    <input
                        name="address"
                        v-model="address1"
                        type="text"
                        class="form-control"
                        required
                    />
                </div>

                <div class="form-group">
                    <label>Address 2</label>
                    <input
                        name="address2"
                        v-model="address2"
                        type="text"
                        class="form-control"
                        placeholder="Optional"
                    />
                    <small class="form-text text-muted"
                        >Enter info like apartment number or door bell here.</small
                    >
                </div>

                <div class="row">
                    <div class="form-group col-sm-5">
                        <label>City</label>
                        <input
                            name="city"
                            v-model="city"
                            type="text"
                            class="form-control"
                            required
                        />
                    </div>

                    <div class="form-group col-sm-2">
                        <label>State</label>
                        <input
                            name="state"
                            v-model="state"
                            type="text"
                            class="form-control"
                            maxlength="2"
                            required
                        />
                    </div>

                    <div class="form-group col-sm-5">
                        <label>ZIP code</label>
                        <input
                            v-mask="'#####'"
                            name="zip"
                            v-model="zip"
                            type="text"
                            class="form-control"
                            maxlength="5"
                            required
                        />
                    </div>
                </div>

                <div class="action-buttons">
                    <button class="hidden"></button>
                    <button
                        class="btn btn-primary"
                        type="submit"
                        v-html="
                            loading
                                ? `<i class='fa fa-spin fa-circle-o-notch mr-2'></i> Processing...`
                                : 'Next'
                        "
                    ></button>
                </div>
            </form>

            <div v-show="currentStep === 1">
                <div class="row">
                    <div class="col-sm-6 ml-0">
                        <h4>Select your billing cycle</h4>
                        <form>
                            <fieldset class="form-group">
                                <div class="row">
                                    <div class="col-12">
                                        <div class="form-check" v-for="p in plans">
                                            <input
                                                class="form-check-input"
                                                type="radio"
                                                :id="p.id"
                                                v-model="plan"
                                                :value="p.id"
                                            />
                                            <label class="form-check-label" :for="p.id">
                                                {{ p.nickname }}
                                                <span
                                                    >(${{ p.amount.toFixed(2) }} per seat per
                                                    {{ p.interval }} plus sales tax where
                                                    applicable)</span
                                                >
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </fieldset>
                        </form>

                        <h4>Enter your billing details</h4>
                        <div class="bg-white border-light p-3 mt-2 mb-3 paymentf">
                            <div class="form-group">
                                <div class="row">
                                    <div class="col">
                                        <label>Card Information</label>
                                        <div id="stripe-card" class="form-control"></div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="action-buttons">
                            <button class="hidden"></button>
                            <div class="w-100 d-flex justify-content-between">
                                <button
                                    id="skip-card"
                                    class="btn btn-sm btn-link text-primary font-weight-bold text-uppercase"
                                    @click.prevent="startTrialNoCard()"
                                >
                                    Skip this step
                                </button>
                                <button
                                    class="btn btn-primary font-weight-bold text-white"
                                    @click.prevent="checkout()"
                                    v-html="
                                        loading
                                            ? `<i class='fa fa-spin fa-circle-o-notch mr-2'></i> Processing...`
                                            : 'Next'
                                    "
                                ></button>
                            </div>
                        </div>
                    </div>
                    <div class="col-sm-6 bg-white p-3">
                        <div class="border-light p-3 mb-4">
                            <div class="d-flex w-100 justify-content-center my-4">
                                <i class="fa fa-check-circle big-blue"></i>
                            </div>

                            <h4 class="text-primary">
                                Your 30 day trial is 100% free and lasts until
                                {{ moment(trialEnd).format("MM/DD/YYYY") }}. Cancel anytime.
                            </h4>

                            <p class="mb-0 trial-features">Your Professional trial includes:</p>
                            <p class="mb-0 trial-features">
                                <i class="fa fa-check mr-3"></i> 10 child profiles
                            </p>
                            <p class="mb-0 trial-features">
                                <i class="fa fa-check mr-3"></i> Unlimited goal creation
                            </p>
                            <p class="mb-0 trial-features">
                                <i class="fa fa-check mr-3"></i> Unlimited data collection on goals
                            </p>
                            <p class="mb-0 trial-features">
                                <i class="fa fa-check mr-3"></i> Graphing and SOAP notes
                            </p>
                            <p class=" trial-features">
                                <i class="fa fa-check mr-3"></i> Unlimited Team Connections
                            </p>
                        </div>

                        <h5 class="text-primary">Will my credit card be charged right now?</h5>
                        <p>
                            No. You won't be charged until after your free trial ends on
                            {{ moment(trialEnd).format("MM/DD/YYYY") }}. After your free trial,
                            you'll be charged US$ {{ selectedPlanPrice }}
                            {{ selectedPlanInterval }} (plus sales tax where applicable) until you
                            decide to downgrade or cancel.
                        </p>

                        <h5 class="text-primary">Can I downgrade my plan?</h5>
                        <p>
                            Yes, you can switch to a new plan, downgrade, or cancel your
                            subscription at any time. Please note, refunds can only be issued within
                            30 days of your subscription start date.
                        </p>
                    </div>
                </div>
            </div>

            <div v-if="currentStep === 2">
                <div class="d-flex align-items-center">
                    <terms-conditions
                        :walkthrough="true"
                        @agree="agree = $event"
                    ></terms-conditions>

                    <div class="mx-5" style="max-width: 14rem;">
                        <b class="text-primary mb-2">No Commitments</b>
                        <p>
                            You can cancel your free trial or monthly subscription at any time by
                            simply visiting the <b>Account Settings page</b>.
                        </p>
                    </div>
                </div>

                <div class="p-3">
                    <p class="mb-1 text-primary font-weight-bold">
                        Please scroll to the bottom and agree to goLOTUS Terms & Conditions
                    </p>
                    <div class="w-100 d-flex justify-content-start my-3">
                        <button
                            class="btn font-weight-bold text-white"
                            :class="agree ? 'btn-primary' : 'btn-light'"
                            @click.prevent="agree ? completeWalkthrough() : null"
                            v-html="
                                loading
                                    ? `<i class='fa fa-spin fa-circle-o-notch mr-2'></i> Processing...`
                                    : 'Start My Free Trial'
                            "
                        ></button>
                    </div>
                </div>
            </div>

            <div v-if="currentStep == 3">
                <h4>Connect to your co-wokers</h4>
            </div>
            <div v-if="currentStep == 4">
                <h4>Connect to your clients</h4>
            </div>
        </div>
    </div>
</template>

<script>
import PhotoUploadWidget from "./widgets/PhotoUploadWidget";
import Breadcrumb from "./walkthrough/Breadcrumb";
import TermsConditions from "./TermsConditions";
import { helpers } from "../plugins/helpers";

export default {
    name: "Walkthrough",
    props: ["user", "organization", "titles"],
    components: {
        Breadcrumb,
        PhotoUploadWidget,
        TermsConditions,
    },
    data() {
        return {
            steps: [
                "Complete account settings",
                "Payment information",
                "Accept Terms of Use",
                //"Connect to your co-workers",
                //"Connect to your clients",
            ],
            currentStep: 0,
            company_name: "",
            title: "",
            title_other: "",
            primary_phone: "",
            profile_photo_path: "",
            biography: "",
            address1: "",
            address2: "",
            city: "",
            state: "",
            zip: "",
            trialEnd: this.moment().add("30", "d"),
            plan: null,
            loading: false,
            agree: false,
            isInvited: false,
            plans: null,
        };
    },
    watch: {
        user() {
            if (this.user) {
                Object.keys(this.user).forEach(k => {
                    if (this[k] || this[k] === "") {
                        this[k] = this.user[k];
                    }
                });
                this.isUserInvited();
            }
        },
    },
    created() {
        this.getBasicPlans();
    },
    mounted() {
        helpers.mountStripeElement();
    },
    computed: {
        selectedPlanPrice() {
            if (this.plan && this.plans.length) {
                return this.plans.find(p => p.id === this.plan).amount.toFixed(2);
            }
            return "";
        },

        selectedPlanInterval() {
            if (this.plan && this.plans.length) {
                return this.plans.find(p => p.id === this.plan).interval;
            }
            return "";
        },
    },
    methods: {
        getBasicPlans() {
            axios.get(`/api/plans`).then(r => {
                this.plans = r.data;
            });
        },

        isUserInvited() {
            axios
                .get("api/invites")
                .then(r => {
                    const invites = r.data.data;
                    let filteredInvites = invites.filter(i => i.email === this.user.email);
                    if (filteredInvites && filteredInvites.length) {
                        this.isInvited = true;
                        this.steps.splice(1, 1);
                    }
                })
                .catch(e => {
                    console.error(e);
                });
        },

        startTrialNoCard() {
            helpers.toggleSpinner("skip-card");
            axios
                .patch(`api/users/${this.user.id}`, { start_trial: 1 })
                .then(r => {
                    this.currentStep++;
                    mixpanel.track(`Subscribe - cardless`);
                })
                .catch(e => {
                    helpers.toggleSpinner("skip-card");
                    console.error(e);
                });
        },

        checkout() {
            this.loading = true;
            let context = this;
            stripe.createToken(card).then(function(result) {
                if (result.hasOwnProperty("error")) {
                    this.loading = false;
                    return;
                }
                if (result.hasOwnProperty("token")) {
                    context.postToApi(result.token);
                }
            });
        },

        postToApi(token) {
            axios
                .post(`/api/users/${this.user.id}/subscription/new`, { token, plan: this.plan })
                .then(r => {
                    this.loading = false;
                    this.currentStep++;
                })
                .catch(e => {
                    this.loading = false;
                    console.error(e);
                });
        },

        saveStep() {
            if (this.currentStep === 0) {
                this.handleAccountSettings();
            }
        },
        showNextStep() {
            if (this.currentStep === this.steps.length - 1) {
                this.completeWalkthrough();
            } else {
                this.currentStep = this.currentStep + 1;
            }
        },
        handleAccountSettings() {
            const data = {};
            const fields = [
                "company_name",
                "title",
                "primary_phone",
                "biography",
                "address1",
                "address2",
                "city",
                "state",
                "zip",
            ];
            this.loading = true;
            if (!this.titles.includes(this.title)) {
                this.title = this.title_other;
            }
            fields.forEach(field => (data[field] = this[field]));
            axios
                .put(`/api/users/${this.user.id}`, data)
                .then(r => {
                    if (this.isInvited) {
                        this.currentStep = 2;
                    } else {
                        this.showNextStep();
                    }
                    this.loading = false;
                })
                .catch(e => {
                    this.loading = false;
                    console.error(e);
                });
        },
        completeWalkthrough() {
            this.loading = true;
            const data = { is_login_walkthrough_completed: 1 };
            axios
                .put(`/api/users/${this.user.id}`, data)
                .then(r => {
                    mixpanel.track("Walkthrough - Complete");
                    // check if there are pending invites
                    // this is important as we may have been referred here from an invite
                    // the only way to tell is to go to that page
                    // if there were no invites, it will auto redirect to /account
                    window.location = "/accept-invite";
                })
                .catch(e => {
                    this.loading = false;
                    console.error(e);
                });
        },
    },
};
</script>

<style scoped lang="scss">
.pricing-plan {
    border: solid 1px #d64571;
    flex-basis: 0;
    flex-grow: 1;
}
.trial-features {
    color: #616161;
    > i {
        color: #003264;
    }
}

.save-tag {
    position: absolute;
    right: -1rem;
    top: 1rem;
    z-index: 2;
    background-color: #fff1c3;
}

.big-blue {
    color: #003264;
    font-size: 5rem;
}

.container {
    //background-color: #fff;
    padding: 30px;
    margin-bottom: 40px;
}
.crumbs {
    margin: 16px 0;
}
.action-buttons {
    display: flex;
    justify-content: space-between;
}
.next-button {
    float: right;
}
.hidden {
    opacity: 0;
}
.space-right {
    margin-right: 10px;
}
.section-header {
    margin: 10px 0;
}
</style>
